// Map a freshly built PROPOSAL to the pending-question entry it implies (or null when the proposal asks
// nothing). One mapping, called wherever proposals surface (runQuery, the plan loop), so no clarify-shaped
// proposal can forget to hold its question open — the exact forgetting that made typed answers re-enter
// the pipeline cold.
//
// Two sources of questions:
//   - CHOICE-shaped proposals (redirect / page choice / export choice): their buttons ARE the options, so
//     the entry mirrors them and a typed answer runs what the matching button would.
//   - proposals carrying an explicit `pendingQuestion` (attached by the proposal builders — today the
//     table clarifies in tableProposal.js): already in entry shape, only the ids are stamped here.
export const questionFromProposal = (proposal, { page, utterance } = {}) => {
    if (!proposal || !page) return null;
    const base = {
        questionId: proposal.planId || proposal.requestId || null,
        page,
        utterance: utterance || proposal.utterance || "",
        question: proposal.message || ""
    };

    // A builder-attached question (table value/column/format clarifies). Attached shape:
    // { kind, multi, options, payload } — see tableProposal.clarify.
    if (proposal.pendingQuestion && Array.isArray(proposal.pendingQuestion.options) && proposal.pendingQuestion.options.length) {
        return { ...base, ...proposal.pendingQuestion };
    }

    // Capability redirect: several pages could run the request — each option carries its navigate+replay
    // plan (see RedirectChoiceCard).
    if (proposal.redirectChoice && (proposal.choices || []).length) {
        return {
            ...base,
            kind: "redirect_choice",
            multi: false,
            options: proposal.choices.map(c => ({ id: c.key, label: c.label, steps: c.steps, target: c.target }))
        };
    }

    // Page choice: filter the dashboard vs open the named page filtered (see PageChoiceCard).
    if (proposal.pageChoice && (proposal.choices || []).length) {
        return {
            ...base,
            kind: "page_choice",
            multi: false,
            options: proposal.choices.map(c => ({ id: c.kind, label: c.label })),
            payload: { target: proposal.target }
        };
    }

    // An outstanding export choice (simple vs the page's variants vs the chooser) — each option already
    // carries the changes it runs (same shape test as tableProposal.isExportChoice).
    if (proposal.table && (proposal.exportChoices || []).length && !(proposal.changes || []).length) {
        return {
            ...base,
            kind: "table_changes",
            multi: false,
            options: proposal.exportChoices.map(c => ({ id: c.kind, label: c.label, changes: c.changes }))
        };
    }

    // A BROWSE result (FTO-10 — the phrase named a filter FIELD, the card shows that field's own options):
    // typed picks apply directly ("energy", "the first one"), and any OTHER word narrows within the field
    // via `options_all` (the full vocabulary, filtered synchronously in the answer path). Each option
    // carries its full match so the executor applies through buildApplyPayload exactly like a card tap.
    const browsed = (((proposal.resolution && proposal.resolution.results) || []).find(r => r && r.browse));
    if (browsed && (browsed.suggestions || []).length) {
        return {
            ...base,
            kind: "field_options",
            multi: true,
            options: browsed.suggestions.map(s => ({ id: String(s.id), label: s.name, match: s })),
            payload: {
                field: browsed.browse.field,
                label: browsed.browse.label,
                value_kind: browsed.browse.value_kind,
                phrase: browsed.phrase,
                total: browsed.browse.total,
                ref: browsed.ref,
                options_all: browsed.browse.options_all || []
            }
        };
    }

    // A THRESHOLD result (FTO-12 — the phrase named a numeric/date field): the typed reply ("over 50k",
    // "construction cost under 1m") is resolved by a pre-matcher hook in answerPendingQuestion that
    // composes `<field label> <reply>` and re-enters the pipeline — the interpreter owns operator and
    // magnitude parsing. The single option below is a placeholder (the store refuses entries without
    // options); the real answer space is free-form.
    const thresh = (((proposal.resolution && proposal.resolution.results) || []).find(r => r && r.threshold));
    if (thresh && (thresh.threshold.fields || []).length) {
        return {
            ...base,
            kind: "field_threshold",
            multi: false,
            options: [{ id: "set", label: `Set a ${thresh.threshold.fields[0].label} threshold` }],
            payload: { phrase: thresh.threshold.phrase, fields: thresh.threshold.fields }
        };
    }

    return null;
};
